import {
  ProductAttributeType,
  ProductDetailFragment,
  ProductDetailVariantFragment,
  StockPolicy,
  SwatchAttributeValue,
} from '@lib/graphql/graphql'
import { splitByCapitalLetters } from '@lib/string'
import { generateMediaUrlV2 } from '@lib/utils'
import { mapEdgesToItems } from '@utils/map'
import { CHANNEL, Country } from 'config'
import { capitalize } from 'lodash-es'
import { Specification } from './ProductSidebar/ProductSidebar'

type TreeNode = {
  name: string
  type: 'Group' | 'Option'
  children?: TreeNode[]
  parent: TreeNode | null
}

export type AttributeGroupType = 'swatch' | 'text'
export type AttributeGroup = {
  type: ProductAttributeType
  groupName: string
  options: OptionType[] | []
}

export type OptionType = {
  id: string
  name: string
  value: string | number | readonly string[]
  label?: string | null
  checked?: boolean
  src?: string | null
  color?: string | null
  disabled?: boolean
}

export const getAvailableOptions = (
  variant: ProductDetailVariantFragment,
  root: TreeNode,
): Map<string, string[]> => {
  const selectedOptions = variant.selectedAttributes.map(
    (selectedAttribute) => ({
      name: selectedAttribute.name,
      value: selectedAttribute.value,
    }),
  )

  const availableOptionsPerLevel = new Map<string, string[]>()

  let currentGroup = root

  selectedOptions.forEach((selectedOption) => {
    const group = dfsFind(currentGroup, selectedOption.name!)

    if (group) {
      // Store all children of the found group as available options
      availableOptionsPerLevel.set(
        selectedOption.name!,
        group.children!.map((child) => child.name),
      )

      // Set the currentGroup to the child that matches the attribute.value
      const optionGroup = group?.children?.find(
        (child) => child.name === selectedOption.value,
      )
      // If optionGroup exists, continue from there, otherwise restart from root
      currentGroup = optionGroup ? optionGroup : root // Reset to root if the path breaks
    } else {
      // console.log(selectedOption)
    }
  })
  return availableOptionsPerLevel
}

function dfsFind(node: TreeNode, name: string): TreeNode | null {
  if (node.name === name) {
    return node
  }
  if (node.children) {
    for (const child of node.children) {
      const found = dfsFind(child, name)
      if (found) {
        return found
      }
    }
  }
  return null
}
const printTree = (node: TreeNode, indent: string = ''): void => {
  node.children.forEach((child) => printTree(child, indent + '  '))
}

const getAttributeTree = (product: ProductDetailFragment): TreeNode | null => {
  const root: TreeNode = {
    name: 'root',
    type: 'Group',
    children: [],
    parent: null,
  }

  if (!product.variants) return root
  let position = root

  const variants = mapEdgesToItems(product.variants)

  variants.forEach((variant) => {
    position = root
    variant.selectedAttributes.forEach((attributeCompacted) => {
      const nodeName = attributeCompacted.name!
      const valueName = attributeCompacted.value ?? ''

      const groupNode = findOrCreateChild(position, nodeName, 'Group')
      const optionNode = findOrCreateChild(groupNode, valueName, 'Option')
      position = optionNode
    })
  })

  mergeDuplicateNodes(root)
  return root.children && root.children.length > 0 ? root.children[0] : null
}

function findOrCreateChild(
  parent: TreeNode,
  name: string,
  type: 'Group' | 'Option',
): TreeNode {
  let child = parent.children?.find(
    (child) => child.name === name && child.type === type,
  )
  if (!child) {
    child = {
      name: name,
      type: type,
      children: [],
      parent: parent,
    }
    parent.children?.push(child)
  }
  return child
}

function mergeDuplicateNodes(root: TreeNode): void {
  const queue: TreeNode[] = [root]
  while (queue.length > 0) {
    const node = queue.shift()!
    if (node.children && node.children.length > 1) {
      const uniqueChildren = mergeChildrenByName(node.children)
      node.children = uniqueChildren
    }
    if (node.children) queue.push(...node.children)
  }
}

function mergeChildrenByName(children: TreeNode[]): TreeNode[] {
  const unique: { [key: string]: TreeNode } = {}
  children.forEach((child) => {
    if (!unique[child.name]) {
      unique[child.name] = child
    } else {
      unique[child.name].children?.push(...child.children!)
      child.children?.forEach((c) => (c.parent = unique[child.name]))
    }
  })
  return Object.values(unique)
}

export const generateAttributeGroupsV2 = (
  product: ProductDetailFragment,
  sku: string,
): AttributeGroup[] => {
  const root: TreeNode | null = getAttributeTree(product)
  if (!root) return []

  // printTree(root)

  const groupCategories = product.attributeAssignments
  const variants = mapEdgesToItems(product.variants)
  const selectedVariant = variants?.find((variant) => variant.sku === sku)
  if (!selectedVariant) return []
  const availableOptionsPerLevel = getAvailableOptions(selectedVariant, root)

  const attributeGroups: AttributeGroup[] | [] =
    groupCategories?.map((group) => {
      const isTextType = mapEdgesToItems(group.values).every(
        (x) =>
          (x as SwatchAttributeValue)?.color === null &&
          (x as SwatchAttributeValue)?.media === null,
      )

      return {
        type: isTextType ? ProductAttributeType.Text : group.attribute.type,
        options: [],
        groupName: group.name ?? '',
      }
    }) ?? []

  attributeGroups.forEach((group) => {
    const optionsSet = collectGroupOptions(root, group.groupName)
    group.options = Array.from(optionsSet)
      //sort by the value position in the product attributes
      .sort((a, b) => {
        const selectedAttribute = product.attributeAssignments.find(
          (attr) => attr.name === group.groupName,
        )

        const options = mapEdgesToItems(selectedAttribute?.values)

        const aIndex = options.findIndex((x) => x.value == a) ?? 0
        const bIndex = options.findIndex((x) => x.value == b) ?? 0
        return aIndex - bIndex
      })
      .flatMap(
        (option) =>
          createOption(
            product,
            selectedVariant,
            group,
            option,
            availableOptionsPerLevel,
            group.type,
          ) ?? [],
      )
  })

  // Ignore the last attribute if its name is "Market Compliance" and it has only one option
  if (
    attributeGroups.length > 0 &&
    attributeGroups[attributeGroups.length - 1].groupName ===
      'Market Compliance' &&
    attributeGroups[attributeGroups.length - 1].options.length === 1
  ) {
    attributeGroups.pop() // Remove the last group
  }

  return attributeGroups
}

function createOption(
  product: ProductDetailFragment,
  variant: ProductDetailVariantFragment,
  group: AttributeGroup,
  option: string,
  availableOptionsPerLevel: Map<string, string[]>,
  optionType: ProductAttributeType,
): OptionType | null {
  const assignedAttribute = product.attributeAssignments.find(
    (attr) => attr.name === group.groupName,
  )
  if (!assignedAttribute || !assignedAttribute.values) return null

  const value = mapEdgesToItems(assignedAttribute.values).find(
    (x) => x.value === option,
  )

  const isDisabled = !availableOptionsPerLevel
    .get(group.groupName)
    ?.includes(option)
  const isChecked = variant.selectedAttributes.some(
    (va) => va.value === option && va.name === group.groupName,
  )

  return {
    id: `${group.groupName}-${option}`,
    name: group.groupName,
    value: option,
    label: value?.value,
    checked: isChecked,
    disabled: isDisabled,
    src:
      optionType === ProductAttributeType.Swatch
        ? (generateMediaUrlV2(
            (value as SwatchAttributeValue)?.media?.src,
          )?.replace('{size}', '120') ?? null)
        : null,
    color:
      optionType === ProductAttributeType.Swatch
        ? ((value as SwatchAttributeValue)?.color ?? null)
        : null,
  }
}

function collectGroupOptions(root: TreeNode, groupName: string): Set<string> {
  const options = new Set<string>()
  bfsTraverse(root, (node) => {
    if (node.type === 'Group' && node.name === groupName) {
      node.children?.forEach((child) => {
        if (child.type === 'Option') {
          options.add(child.name)
        }
      })
    }
  })
  return options
}

function bfsTraverse(root: TreeNode, callback: (node: TreeNode) => void): void {
  const queue: TreeNode[] = [root] // Initialize the queue with the root node

  while (queue.length > 0) {
    const current = queue.shift()! // Get the first node in the queue

    callback(current) // Execute the callback function on the current node

    // Enqueue all children of the current node
    current.children?.forEach((child) => {
      queue.push(child)
    })
  }
}

export const getVariantFromSelectedOptions = (
  product: ProductDetailFragment,
  selectedOptions: HTMLInputElement[],
): ProductDetailVariantFragment => {
  const variants = mapEdgesToItems(product.variants)
  const validVariant = variants?.find((variant) =>
    variant.selectedAttributes.every((attr) =>
      selectedOptions.find(
        (option) => option.name == attr.name && option.value == attr.value,
      ),
    ),
  )
  if (validVariant) return validVariant

  const sortedSwatches = selectedOptions
    .sort((a, b) => {
      const aIndex = product.attributeAssignments.findIndex(
        (x) => x.name == a.name,
      )
      const bIndex = product.attributeAssignments.findIndex(
        (x) => x.name == b.name,
      )
      return aIndex - bIndex
    })
    .map((x) => ({ groupName: x.name, attributeValue: x.value }))

  const variantScores = variants?.map((variant) => {
    let score = 0
    for (let i = 0; i < sortedSwatches.length; i++) {
      const { groupName, attributeValue } = sortedSwatches[i]
      const matching = variant.selectedAttributes.find(
        (attr) => attr.name == groupName && attr.value == attributeValue,
      )

      if (matching) {
        const diff = sortedSwatches.length - i
        score += Math.pow(diff, 5)
      }
    }
    return { variant, score }
  })

  const bestVariant = variantScores!.sort((a, b) => b.score - a.score)[0]
    .variant
  return bestVariant
}

export const getProductName = (name: string) => {
  const splitName = name?.split(',')
  if (!splitName) return null
  return {
    name: splitName.find((x) => x) ?? '',
    secondaryName: splitName.length > 1 ? splitName.pop() : '',
  }
}

export const refineProductTitle = (title: string) => {
  // Initial cleanup to remove attribute names, and regional indicators
  let refinedTitle = title
    .replace(/\b[^:,()]+:\s*([^:,()]+)/g, '$1')
    .replace(/, EU|, US|, UK|EU, |US, |UK, /g, '')
    .replace(/[,| ]{0,2}Price Grp. [A-Z]/g, '')

  // Secondary Name, everything after first opening paranthesis
  let secondaryName = refinedTitle
  let innerContent = ''
  let startOfFinalPartIdx = refinedTitle.indexOf('(')

  const startIdx = secondaryName.indexOf('(')
  let balance = 0
  for (let idx = startIdx; idx < secondaryName.length; idx++) {
    if (!secondaryName[idx]) continue

    if (secondaryName[idx] === '(') {
      if (balance == 0) {
        innerContent = ''
        startOfFinalPartIdx = idx
      }
      balance++
    }
    if (secondaryName[idx] === ')') balance--

    if (balance >= 0) {
      innerContent += secondaryName[idx]
    } else {
      balance = 0
    }
  }

  if (innerContent.startsWith('(')) {
    innerContent = innerContent.slice(1)
    if (innerContent.endsWith(')')) {
      innerContent = innerContent.slice(0, -1)
    }
  }

  // Primary Name
  let primaryName = refinedTitle.substring(0, startOfFinalPartIdx)

  // Remove trailing comma from primary name
  primaryName = primaryName.replace(/,$/, '')

  const regexifyItem = (currItem: string) => {
    let item = currItem.trim()
    // (\([A-Za-z0-9 ]+)\, Standard(,|\))
    const regex = /(\([A-Za-z0-9 ]+)\, Standard(,|\))/gm
    const match = regex.exec(item)
    if (match) {
      // // if grp 2 is comma, remove it and replace whole match with group 1
      // // if grp 2 is closing paranthesis, replace whole match group 1 + group 2
      item = item.replace(
        match[0],
        match[2] == ',' ? match[1] : match[1] + match[2],
      )
    }
    return item
  }

  let hashSet = new Set()
  let currItem = ''
  balance = 0
  for (let i = 0; i < innerContent.length; i++) {
    if (balance == 0 && innerContent[i] == ',') {
      if (!currItem.toLowerCase().includes('foam')) {
        hashSet.add(regexifyItem(currItem))
      }
      currItem = ''
      continue
    }

    if (innerContent[i] == '(') balance++
    if (innerContent[i] == ')') balance--

    currItem += innerContent[i]
  }
  if (currItem != '' && !currItem.toLowerCase().includes('foam'))
    hashSet.add(regexifyItem(currItem))

  secondaryName = [...hashSet].join(', ')

  return {
    primaryName: primaryName,
    secondaryName: secondaryName ? secondaryName : '',
    combinedName: `${primaryName}${secondaryName ? ` (${secondaryName})` : ''}`,
  }
}

export const isContentImage = (fileName?: string) => {
  if (!fileName) return false
  const splitName = fileName.split('_')

  if (splitName.length < 1) return false

  return (
    splitName[1]?.toLowerCase().startsWith('c') ||
    splitName[1]?.toLowerCase().startsWith('l')
  )
}
const addSpaceBeforeAndAfterLetter = (str: string, letter: string) => {
  const regex = new RegExp(`(${letter})`, 'g')
  return str.replace(regex, ' $1 ')
}

export const getSpecifications = (
  metadata: ProductDetailVariantFragment['metadata'],
): Specification[] => {
  const specifications = metadata
    ?.filter((x) => x.key.startsWith('ODOO_SPEC_'))
    .map((x) => {
      var specificationName = x.key.split('ODOO_SPEC_')[1]
      return {
        label: capitalize(splitByCapitalLetters(specificationName)).replace(
          'dx wx h',
          'D x W x H',
        ),
        value: addSpaceBeforeAndAfterLetter(x.value, 'x'),
      }
    })
  return specifications
}

// getPurchaseDisabled
export const getPurchaseDisabled = (
  selectedVariant: ProductDetailVariantFragment,
  country: Country,
) => {
  if (!country.ecommerceEnabled) return true
  if (!selectedVariant.price) return true
  if (!selectedVariant.availability.availableForPurchase) return true

  return (
    selectedVariant.availability.availableQuantity <= 0 &&
    selectedVariant.availability.stockPolicy == StockPolicy.Tracked
  )
}

export const getStockAvailabilityText = ({
  stockState,
  channel,
}: {
  channel: CHANNEL
  stockState: string
}) => {
  switch (stockState) {
    case 'In stock':
      return {
        circleColor: '#008D30',
        mainText: 'In Stock',
        secondaryText:
          channel == CHANNEL.EUROPE || channel == CHANNEL.UNITED_KINGDOM
            ? ''
            : '',
      }

    case 'Remote stock':
      return {
        circleColor: '#D1A44F',
        mainText: 'Remote Stock',
        secondaryText:
          channel == CHANNEL.EUROPE || channel == CHANNEL.UNITED_KINGDOM
            ? '1-4 weeks'
            : //TODO: FIX IF US REMOTE WAREHOUSE / MADE TO ORDER
              '',
      }

    case 'Made to order':
      return {
        circleColor: '#D1A44F',
        mainText: 'Made to Order',
        secondaryText:
          channel == CHANNEL.EUROPE || channel == CHANNEL.UNITED_KINGDOM
            ? '1-3 months'
            : //TODO: FIX IF US REMOTE WAREHOUSE / MADE TO ORDER
              '',
      }
  }
}
